import { NextBestDecision } from "../../../models/home";
import { buildAction, buildCompactAction, buildConfidence, buildPriority, buildRisk, buildSummary, isoFromNow } from "./home-qa-harness.builders";

export const READY_HERO: NextBestDecision = {
  decision: {
    decisionId: "decision-ready",
    state: "act_now",
    title: "Approve supplier invoice for payment",
    shortReason: "The payment deadline is today and the evidence set is complete.",
    documentCount: 3,
    primaryDocument: {
      documentId: "doc-ready",
      classificationId: "class-ready",
      title: "Supplier invoice packet"
    },
    nextBestAction: buildCompactAction("process_invoice", "Process invoice", true),
    availableActions: [
      buildCompactAction("process_invoice", "Process invoice", true, {
        placement: "primary",
        riskLevel: "low",
        executionMode: "direct",
        refreshHints: ["overview", "next", "list", "detail", "activity"]
      })
    ],
    priority: buildPriority(96, "critical", "Deadline is inside the current workday."),
    risk: buildRisk("high", "Missing the deadline may create a late fee."),
    confidence: buildConfidence(0.94),
    missingInformationCount: 0,
    owner: { userId: "u-1", displayName: "Finance Ops" },
    dueAt: isoFromNow(8),
    updatedAt: isoFromNow(-1),
    traceId: "trace-ready"
  },
  nextAction: buildAction("process_invoice", "Process invoice", true, {
    description: "Post the invoice to the ERP and mark the billing workflow as complete.",
    completionCriteria: ["Validate supplier", "Confirm invoice number", "Send to ERP"]
  }),
  summary: buildSummary()
};

export const BLOCKED_HERO: NextBestDecision = {
  decision: {
    decisionId: "decision-blocked",
    state: "waiting",
    title: "Process customer reimbursement",
    shortReason: "The reimbursement can move forward only after a missing account code is confirmed.",
    documentCount: 2,
    primaryDocument: {
      documentId: "doc-blocked",
      classificationId: "class-blocked",
      title: "Customer reimbursement request"
    },
    nextBestAction: buildCompactAction("process_reimbursement", "Process reimbursement", true),
    availableActions: [
      buildCompactAction("confirm_missing_information", "Review missing information", true, {
        kind: "remediation",
        placement: "primary",
        requiresInput: true,
        executionMode: "detail",
        refreshHints: ["detail", "activity"]
      })
    ],
    priority: buildPriority(82, "high", "The reimbursement SLA expires in the next 48 hours."),
    risk: buildRisk("medium", "Delay would affect customer trust and support load."),
    confidence: buildConfidence(0.9),
    missingInformationCount: 2,
    owner: { userId: "u-2", displayName: "Customer Ops" },
    dueAt: isoFromNow(36),
    updatedAt: isoFromNow(-2),
    traceId: "trace-blocked"
  },
  nextAction: buildAction("process_reimbursement", "Process reimbursement", true, {
    description: "Run the reimbursement in the finance system once all required fields are confirmed."
  }),
  summary: buildSummary()
};

export const DISABLED_HERO: NextBestDecision = {
  decision: {
    decisionId: "decision-disabled",
    state: "act_now",
    title: "Sync approved vendor record",
    shortReason: "The vendor approval is complete but the downstream connector is still unavailable.",
    documentCount: 1,
    primaryDocument: {
      documentId: "doc-disabled",
      classificationId: "class-disabled",
      title: "Vendor approval packet"
    },
    nextBestAction: buildCompactAction("sync_vendor", "Sync vendor record", false, {
      disabledReason: "The ERP connector is still syncing the supplier account."
    }),
    availableActions: [
      buildCompactAction("sync_vendor", "Sync vendor record", false, {
        placement: "primary",
        disabledReason: "The ERP connector is still syncing the supplier account.",
        executionMode: "detail"
      })
    ],
    priority: buildPriority(88, "high", "The sync is still time-sensitive for the onboarding window."),
    risk: buildRisk("high", "Onboarding remains incomplete until the vendor sync finishes."),
    confidence: buildConfidence(0.91),
    missingInformationCount: 0,
    owner: { userId: "u-3", displayName: "Procurement" },
    dueAt: isoFromNow(16),
    updatedAt: isoFromNow(-4),
    traceId: "trace-disabled"
  },
  nextAction: buildAction("sync_vendor", "Sync vendor record", false, {
    disabledReason: "The ERP connector is still syncing the supplier account.",
    description: "The action is valid, but the external system is not ready yet."
  }),
  summary: buildSummary()
};

export const LOW_CONFIDENCE_HERO: NextBestDecision = {
  decision: {
    decisionId: "decision-feedback",
    state: "act_soon",
    title: "Confirm contract renewal path",
    shortReason: "The recommendation is plausible but confidence is still below the review threshold.",
    documentCount: 2,
    primaryDocument: {
      documentId: "doc-feedback",
      classificationId: "class-feedback",
      title: "Master services agreement"
    },
    nextBestAction: buildCompactAction("renew_contract", "Renew contract", true),
    availableActions: [
      buildCompactAction("review_evidence", "Review evidence", true, {
        kind: "inspection",
        placement: "primary",
        executionMode: "inspect",
        refreshHints: ["detail", "activity"]
      })
    ],
    priority: buildPriority(74, "medium", "The notice window starts later this week."),
    risk: buildRisk("medium", "Wrong routing would generate contract churn."),
    confidence: buildConfidence(0.68, ["Renewal clause confidence is still low."]),
    missingInformationCount: 0,
    owner: { userId: "u-4", displayName: "Legal Ops" },
    dueAt: isoFromNow(60),
    updatedAt: isoFromNow(-6),
    traceId: "trace-feedback"
  },
  nextAction: buildAction("renew_contract", "Renew contract", true, {
    description: "Send the renewal package after confirming the extracted clause."
  }),
  summary: buildSummary()
};

